import React, { useMemo, useRef, Suspense } from 'react';
import { Edges, Billboard, Text } from '@react-three/drei';
import PalletStack from './PalletStack';
import { useFrame } from '@react-three/fiber';

const normalizeEntityType = (name) =>
    (name || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");

const isRackEntity = (name) => normalizeEntityType(name) === "rack";
const isStorageLocation = (name) => ["bin", "grid", "subgrid"].includes(normalizeEntityType(name));
const isZoneEntity = (name) => ["zone", "open zone", "openzone"].includes(normalizeEntityType(name));

export const ArrowPin = ({ position }) => {
  const groupRef = useRef();
  const glowRef = useRef();


  const darkColor = "#FF6600";

  useFrame(({ clock }) => {
    // Pulsating scale and fade for the glow ring
    const pulse = 1.8 + Math.sin(clock.getElapsedTime() * 2.7) * 0.4;
    const fade = 0.5 + Math.abs(Math.sin(clock.getElapsedTime() * 2.7)) * 0.3;

    if (glowRef.current) {
      glowRef.current.scale.set(pulse, pulse, pulse);
      glowRef.current.material.opacity = fade;
    }

    if (groupRef.current) {
      groupRef.current.position.y = position[1] + Math.sin(clock.getElapsedTime() * 1.7) * 0.4;
      groupRef.current.rotation.y = Math.sin(clock.getElapsedTime() * 1.4) * 0.25;
    }
  });

    const poleHeight = 4.5;
    const arrowHeight = 2.4;

  return (
    <group ref={groupRef} position={position}>

      {/* Thick tall pole */}
      <mesh position={[0, 3.5, 0]}>
        <cylinderGeometry args={[0.4, 0.4, poleHeight, 28]} />
        <meshStandardMaterial
          color={darkColor}
          emissive={darkColor}
          emissiveIntensity={1.8}
          metalness={0}
          roughness={0.4}
        />
      </mesh>

      {/* Large arrowhead pointing down */}
      <mesh position={[0, 0, 0]} rotation={[Math.PI, 0, 0]}>
        <coneGeometry args={[1.5, arrowHeight, 28]} />
        <meshStandardMaterial
          color={darkColor}
          emissive={darkColor}
          emissiveIntensity={2}
          metalness={0}
          roughness={0.2}
        />
      </mesh>
    </group>
  );
};


const Bin = ({ bin,
                allBins, 
                onBinClick, 
                selectedBin, 
                setLegendData, 
                selectedEntity, 
                color, 
                opacity, 
                parentZoneId, 
                palletVariant = 'full', 
                boxesConfig, 
                palletHeightOverride, 
                palletsAlongZ = 1, 
                palletsGapZ = 0.5, 
                palletsAlongX = 1, 
                palletsGapX = 0.5, 
                squarePallet = false, 
                boxInsetX = 2, 
                boxInsetZ = 2, 
                innerPadOverride, 
                fitRatioOverride, 
                tiersAlongY = 1, 
                tiersGapY = 0  }) => {
  const { width, height, length, xco, yco, zco, id } = bin;
  const adjustedY = yco + height / 2;
  const highlight = bin?.highlight;
  const isSelected = selectedBin?.id === id;
  const isEmpty = bin?.is_bin_empty;
  const darkColor = "#FF6600";

  const handleClickBin = (e) => {
    e.stopPropagation();
    onBinClick(prev => (prev?.id === bin.id ? null : bin));
  };

  let colorCase;
  if (highlight) colorCase = 'yellow';
  else if (isSelected) colorCase = '#8334eb';
  else if (isEmpty) colorCase = '#f2f2f2';
  else colorCase = color;

  // Detect OpenZone bins (by naming convention) for in-component labeling
const isOpenZoneBinLabel = useMemo(() => {
  const name = (bin?.en_name || "").toLowerCase().trim();

  return (
    ["open zone", "openzone", "grid", "bin"].some(type =>
      name.includes(type)
    ) ||
    !!bin?.palletConfig
  );
}, [bin]);

// Detect whether this storage location belongs to an Open Zone before applying the RAK layout
const isOpenZoneStorageLocation = useMemo(() => {
  const parentName = (bin?.parentEntity?.en_name ?? bin?.parent?.en_name ?? "").toLowerCase().trim();
  const entityName = (bin?.en_name ?? "").toLowerCase().trim();
  return parentName === "open zone" || parentName === "openzone" || entityName === "grid";
}, [bin]);

  const gridEdgeColor = isSelected ? '#FF6600' : 'white';
  const existingGridOpacity = (typeof opacity === 'number' && opacity >= 0.1 && opacity <= 0.2) ? opacity : 0.15;
  const gridOpacity = isOpenZoneStorageLocation ? existingGridOpacity : (isEmpty ? 0.75 : 0.2);
  const gridColor = isOpenZoneStorageLocation ? color : colorCase;

  // Determine if this bin should render internal contents based on selectedEntity
  const shouldRenderContents = useMemo(() => {
    const sel = selectedEntity?.entity;
    if (!sel) return true; // no selection -> render all contents

    const type = sel.en_name;
    const selId = sel.id;

    // Common identifiers coming from API (may be absent for synthetic OpenZone bins)
    const bZoneId = bin.zone_id ?? bin.zoneId ?? bin.parent_zone_id ?? parentZoneId;
    const bRackId = bin.rack_id ?? bin.rackId ?? bin.parent_rack_id;
    const bParentId = bin.parent_entity_id;

    if (isZoneEntity(type)) {
      // If linkage exists, match by zone/rack/parent relationships
      return (
        bZoneId === selId ||
        bParentId === selId ||
        parentZoneId === selId
      );
    }
    if (isRackEntity(type)) {
      return bRackId === selId || bParentId === selId;
    }
    if (isStorageLocation(type)) {
      return bin.id === selId;
    }
    // Unknown selected entity type: hide contents by default
    return false;
  }, [selectedEntity, bin, parentZoneId]);

  const isTopBinHighlightedInColumn = useMemo(() => {
    if (!allBins) return false;
    const binsInColumn = allBins.filter(b => b.xco === xco && b.zco === zco);
    const anyHighlighted = binsInColumn.some(b => b.highlight);
    const topBin = binsInColumn.reduce((tb, cb) =>
      (cb.yco + cb.height) > (tb.yco + tb.height) ? cb : tb
    );
    return anyHighlighted && topBin.id === id;
  }, [allBins, xco, zco, yco, height, id]);

  // Compute interior footprint for pallets with small margins
  // Prefer API-driven layout overrides first, then props, then defaults
  const pad = typeof (bin?.palletConfig?.innerPadOverride) === 'number'
    ? bin.palletConfig.innerPadOverride
    : (typeof innerPadOverride === 'number' ? innerPadOverride : 1.0);
  const fitRatio = typeof (bin?.palletConfig?.fitRatioOverride) === 'number'
    ? bin.palletConfig.fitRatioOverride
    : (typeof fitRatioOverride === 'number' ? fitRatioOverride : 0.9);
 // use more of the inner footprint by default
  const palletFootprint = {
    width: Math.max(6, (width - 2 * pad) * fitRatio),
    length: Math.max(6, (length - 2 * pad) * fitRatio),
  };
  // Single cardboard box per pallet, sized slightly smaller than pallet and height-fit inside bin
  const palletHeight = (typeof (bin?.palletConfig?.palletHeightOverride) === 'number')
    ? bin.palletConfig.palletHeightOverride
    : (typeof palletHeightOverride === 'number' ? palletHeightOverride : 0.9); // allow override (e.g., OpenZone)
  const clearance = 0.0;       // flush with bin floor and ceiling calculations
  const boxLift = 0.2;         // PalletStack lite lifts box by 0.2 above pallet
  const availableHeight = Math.max(3, height - 2 * clearance);
  // Single tier: apply scale factor to reduce box height while fitting inside available height
  const boxScaleFactor = 0.2; // 30% of the free height to leave room for another pallet
  let computedBoxHeight = Math.max(0.5, (availableHeight - palletHeight - boxLift) * boxScaleFactor);

  // Prefer API-provided pallet/boxes config if present
  const apiCfg = bin?.palletConfig || bin?.layout || {};
  const effectiveVariant = apiCfg.palletVariant ?? palletVariant;
  const resolvedBoxes =
    (boxesConfig ?? apiCfg.boxesConfig) ??
    (isOpenZoneStorageLocation
      ? { cols: 3, rows: 3, layers: 1, gap: 0.2 }
      : { cols: 1, rows: 1, layers: 1, gap: 0 });
  const insetX = apiCfg.boxInsetX ?? boxInsetX;
  const insetZ = apiCfg.boxInsetZ ?? boxInsetZ;

  // Helper to compute per-box footprint to fit grid on a given pallet size
  const gridGap = resolvedBoxes.gap ?? 0;
  const computeBoxDims = (palW, palL) => {
    const boxWidthFit = (palW - (resolvedBoxes.cols - 1) * gridGap) / resolvedBoxes.cols;
    const boxLengthFit = (palL - (resolvedBoxes.rows - 1) * gridGap) / resolvedBoxes.rows;
    return {
      width: Math.max(2, boxWidthFit - insetX),
      length: Math.max(2, boxLengthFit - insetZ),
      height: computedBoxHeight,
    };
  };
  const tierY0 = yco + clearance; // yco is bin base; sit just above bin floor

  // Clamp function for box height so content always stays inside bin (supports per-tier baseY)
  const clampBoxHeight = (boxH, baseY = tierY0) => {
    const maxContentTop = yco + height - clearance; // top of bin minus clearance
    let newBoxH = boxH;
    let contentTop = baseY + palletHeight + newBoxH + boxLift;
    if (contentTop > maxContentTop) {
      newBoxH = Math.max(0.5, maxContentTop - baseY - palletHeight - boxLift);
    }
    return newBoxH;
  };

  return (
    <group>
      {/* Semi-transparent bin to reveal internal pallets */}
        <mesh position={[xco, adjustedY, zco]} onClick={handleClickBin}>
          <boxGeometry args={[width, height, length]} />
          <meshStandardMaterial transparent opacity={gridOpacity} color={gridColor} depthWrite={true} />
          <Edges color={isOpenZoneStorageLocation ? gridEdgeColor : (isEmpty ? '#bdbdbd' : (isSelected ? '#FF6600' : 'white'))} threshold={0.5} scale={1.001} lineWidth={opacity ? (isSelected ? 4 : 1) : 0.3} />
        </mesh>

      {/* Pallets grid inside the bin: side-by-side along X, along Z, and stacked vertically based on API */}
      {isEmpty && shouldRenderContents && (() => {
          // Prefer API-driven layout if present
          const countZ = Math.max(1, Math.floor((apiCfg.palletsAlongZ ?? palletsAlongZ)));
          const countX = Math.max(1, Math.floor((apiCfg.palletsAlongX ?? palletsAlongX)));
          const totalGapZ = (countZ - 1) * (apiCfg.palletsGapZ ?? palletsGapZ);
          const totalGapX = (countX - 1) * (apiCfg.palletsGapX ?? palletsGapX);
          let perLen = Math.max(2, (palletFootprint.length - totalGapZ) / countZ);
          let perWid = Math.max(2, (palletFootprint.width - totalGapX) / countX);
          // If squarePallet, make both dimensions equal to the limiting side
          const useSquare = apiCfg.squarePallet ?? squarePallet;
          if (useSquare) {
            const side = Math.max(2, Math.min(perLen, perWid));
            perLen = side;
            perWid = side;
          }
          const startZ = zco - (countZ * perLen + totalGapZ) / 2 + perLen / 2;
          const startX = xco - (countX * perWid + totalGapX) / 2 + perWid / 2;

          const items = [];

          if (isOpenZoneStorageLocation) {
            // Open Zone (RAK Ceramics) pallet matrix layout
            const palletGap = apiCfg.palletGap ?? 1;
            const outerMargin = apiCfg.outerMargin ?? 2;
            // Nominal yard stack footprint (uses PalletStack default box footprint)
            const nominalPW = Math.max(2, apiCfg.palletWidth ?? 8);
            const nominalPL = Math.max(2, apiCfg.palletLength ?? 10);
            const usableW = Math.max(1, width - 2 * outerMargin);
            const usableL = Math.max(1, length - 2 * outerMargin);
            const cols = Math.max(1, Math.floor(usableW / (nominalPW + palletGap)));
            const rows = Math.max(1, Math.floor(usableL / (nominalPL + palletGap)));
            // Stretch cells to fill the Grid with only a small outer margin and gap
            const cellW = cols === 1 ? usableW : (usableW - (cols - 1) * palletGap) / cols;
            const cellL = rows === 1 ? usableL : (usableL - (rows - 1) * palletGap) / rows;
            const startX = xco - width / 2 + outerMargin + cellW / 2;
            const startZ = zco - length / 2 + outerMargin + cellL / 2;
            const baseBoxDims = computeBoxDims(cellW, cellL);
            // Vertical pallet levels per stack: use palletConfig.tiersAlongY if specified, capped at 3, else 3
            const tiers = Math.max(1, Math.min(3, Math.floor(apiCfg.tiersAlongY ?? 3)));
            const tierGapY = apiCfg.tiersGapY ?? 0;
            for (let r = 0; r < rows; r++) {
              for (let c = 0; c < cols; c++) {
                const px = startX + c * (cellW + palletGap);
                const pz = startZ + r * (cellL + palletGap);
                for (let t = 0; t < tiers; t++) {
                  const py = tierY0 + t * (palletHeight + baseBoxDims.height + boxLift + tierGapY);
                  const clampedH = clampBoxHeight(baseBoxDims.height, py);
                  if (clampedH < 0.5) break;
                  const tierBox = { ...baseBoxDims, height: clampedH };
                  items.push(
                    <PalletStack
                      key={`oz-pal-${r}-${c}-${t}`}
                      position={[px, py, pz]}
                      pallet={{ width: cellW, length: cellL, height: palletHeight }}
                      box={tierBox}
                      boxes={resolvedBoxes}
                      colors={{ wood: '#8b5a2b', box: '#DCC488' }}
                      opacity={1}
                      variant={effectiveVariant}
                    />
                  );
                }
              }
            }
          } else {
            for (let iz = 0; iz < countZ; iz++) {
              for (let ix = 0; ix < countX; ix++) {
                const px = startX + ix * (perWid + (apiCfg.palletsGapX ?? palletsGapX));
                const pz = startZ + iz * (perLen + (apiCfg.palletsGapZ ?? palletsGapZ));
                const baseBoxDims = computeBoxDims(perWid, perLen);
                // Vertical tiers: prefer API count/gap (fallback to two tiers if api specifies 2, else default 1)
                const tiers = Math.max(1, Math.floor(apiCfg.tiersAlongY ?? 2));
                const gapY = apiCfg.tiersGapY ?? 0; // user requested no gap by default
                const tierOffsetY = palletHeight + baseBoxDims.height + boxLift + gapY;
                for (let iy = 0; iy < tiers; iy++) {
                  const py = tierY0 + iy * tierOffsetY;
                  const clampedH = clampBoxHeight(baseBoxDims.height, py);
                  if (clampedH < 0.5) break;
                  const tierBox = { ...baseBoxDims, height: clampedH };
                  items.push(
                    <PalletStack
                      key={`pal-${iz}-${ix}-${iy}`}
                      position={[px, py, pz]}
                      pallet={{ width: perWid, length: perLen, height: palletHeight }}
                      box={tierBox}
                      boxes={resolvedBoxes}
                      colors={{ wood: '#8b5a2b', box: '#DCC488' }}
                      opacity={1}
                      variant={effectiveVariant}
                    />
                  );
                }
              }
            }
          }
          return items;
        })()}

      {/* Always-visible label for OpenZone bins to avoid occlusion issues */}
      {isOpenZoneBinLabel && (
        <Suspense fallback={null}>
          <Billboard position={[xco, (yco ?? 0) + ((typeof height === 'number' && !Number.isNaN(height)) ? height : 10) + 5, zco]} renderOrder={9999} frustumCulled={false}>
            <Text
              fontSize={3}
              fontWeight={800}
              color="black"
              anchorX="center"
              anchorY="bottom"
              renderOrder={9999}
              depthTest={false}
              depthWrite={false}
              toneMapped={false}
              maxWidth={100}
              whiteSpace="overflowWrap"
              outlineWidth={0.04}
              outlineColor="#ffffff"
              outlineOpacity={0.9}
              frustumCulled={false}
            >
            {(bin.label || bin.en_name || 'Bin') + ` (${bin.entity_name})`}
            </Text>
          </Billboard>
        </Suspense>
      )}

      {isTopBinHighlightedInColumn && (
        <Suspense fallback={null}>
          <Billboard position={[xco, yco + height + 4, zco]}>
            <ArrowPin position={[0, 0, 0]} />
          </Billboard>
        </Suspense>
      )}
    </group>
  );
};

export default Bin;
